import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appointmentsApi } from '@/services/api';
import type { CreateAppointmentRequest } from '@/types/appointment';

export const appointmentKeys = {
  all: ['appointments'] as const,
  lists: () => [...appointmentKeys.all, 'list'] as const,
  list: (filters: string) => [...appointmentKeys.lists(), { filters }] as const,
  unassigned: () => [...appointmentKeys.all, 'unassigned'] as const,
  details: () => [...appointmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...appointmentKeys.details(), id] as const,
};

// Any mutation that creates, edits, claims, or releases a slot affects both the
// full list and the unassigned-only view, so invalidate both together.
const invalidateAppointments = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
  queryClient.invalidateQueries({ queryKey: appointmentKeys.unassigned() });
};

export function useAppointments() {
  return useQuery({
    queryKey: appointmentKeys.lists(),
    queryFn: appointmentsApi.getAppointments,
  });
}

export function useCreateAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: appointmentsApi.createAppointment,
    onSuccess: () => invalidateAppointments(queryClient),
  });
}

export function useUpdateAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateAppointmentRequest> }) =>
      appointmentsApi.updateAppointment(id, data),
    onSuccess: () => invalidateAppointments(queryClient),
  });
}

export function useDeleteAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: appointmentsApi.deleteAppointment,
    onSuccess: () => invalidateAppointments(queryClient),
  });
}

export function useClaimAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: appointmentsApi.claimAppointment,
    onSuccess: () => invalidateAppointments(queryClient),
  });
}

export function useUnclaimAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: appointmentsApi.unclaimAppointment,
    onSuccess: () => invalidateAppointments(queryClient),
  });
}

export function useAdminUnassignAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: appointmentsApi.adminUnassignAppointment,
    onSuccess: () => invalidateAppointments(queryClient),
  });
}
